import { useState } from "react";
import { useDbClient, useLiveInfiniteQuery } from "@tanstack/react-db";
import { ForumFeedView } from "@softmax/web-ux/forum";
import type { PostFeedPage } from "@softmax/observatory-client/types";
import {
  communityNavigation,
  communityAccess,
  postImageContentTypes,
} from "@softmax/web-features/community";
import {
  collectionError,
  refreshCollections,
} from "@softmax/web-features/data";
import { curatedText } from "@softmax/web-features/coworlds";
import { postFeedCollection, type PostSort } from "./post.collections";
import { voteForPost, refreshPostFeed } from "./post.mutations";
import { WikiRail } from "./forum-wiki-rail";
import { renderLink } from "./forum-links";
import { feedState } from "./forum-read-state";
import { useForumPostCompose } from "./use-forum-post-compose";
import { useForumVotes } from "./use-forum-votes";
import type { RoomProps } from "./forum-room.types";

export function ForumFeed({
  elevatedPrivileges = false,
  layout = "page",
  showHeader = true,
  navigationSearch = "",
  composeEnabled = false,
  coworld,
  forumSlug = coworld,
  displayName,
  description,
  initialPage = null,
  basePath,
  wikiBasePath,
  home,
  lede,
}: RoomProps & {
  initialPage?: PostFeedPage | null;
  lede?: string | undefined;
  showHeader?: boolean;
}) {
  const navigation = communityNavigation({
    basePath,
    navigationSearch,
  });
  const db = useDbClient();
  const [sort, setSort] = useState<PostSort>("hot");
  const scope = { forumSlug, limit: 50, sort, elevatedPrivileges };
  const descriptor = postFeedCollection(scope);
  const initialData =
    sort === "hot"
      ? initialPage?.entries.map((post, feedPosition) => ({
          ...post,
          feedPosition,
        }))
      : undefined;
  const source = db.collection(
    descriptor,
    initialData === undefined ? {} : { initialData },
  );
  const feed = useLiveInfiniteQuery(
    (q) =>
      q
        .from({ post: source })
        .orderBy(({ post }) => post.feedPosition)
        .orderBy(({ post }) => post.id),
    { pageSize: 50 },
  );
  const { accessLost } = communityAccess([feed.health]);
  const [refreshing, setRefreshing] = useState(false);
  const { votes, vote } = useForumVotes({
    submit: ({ id, value }) =>
      voteForPost({
        db,
        postId: id,
        value,
        elevatedPrivileges,
        forumSlug,
        feedScope: scope,
      }),
  });
  const { composeBusy, composeError, submitPost } = useForumPostCompose(scope);
  return (
    <ForumFeedView
      layout={layout}
      showHeader={showHeader}
      name={curatedText(displayName ?? coworld)}
      description={description ? curatedText(description) : undefined}
      lede={lede}
      homeHref={home}
      wikiRail={
        layout === "page" ? (
          <WikiRail slug={forumSlug} basePath={wikiBasePath ?? basePath} />
        ) : undefined
      }
      state={feedState({
        posts: feed.data,
        ready: feed.isReady,
        error: collectionError(feed.health),
        accessLost,
        refreshing,
        syncError: feed.isSyncError,
        hasMore: feed.hasNextPage,
        loadingMore: feed.isFetchingNextPage,
        votes,
        elevatedPrivileges,
      })}
      sort={sort}
      onSortChange={setSort}
      onRefresh={() => {
        setRefreshing(true);
        const request = refreshCollections({
          signal: db.requireDependency<AbortController>("lifetime").signal,
          queries: [feed.collection],
          refresh: () => refreshPostFeed({ db, scope }),
        });
        void request.finally(() => setRefreshing(false));
      }}
      onLoadMore={() => void feed.fetchNextPage()}
      onVote={({ postId, direction }) => vote({ id: postId, direction })}
      votes={votes}
      postHref={(postId) => navigation.forumHref({ postId })}
      renderLink={renderLink}
      compose={
        composeEnabled && !accessLost
          ? {
              busy: composeBusy,
              error: composeError,
              onSubmit: submitPost,
              allowPlainText: elevatedPrivileges,
              imageAccept: elevatedPrivileges
                ? postImageContentTypes.join(",")
                : undefined,
            }
          : undefined
      }
    />
  );
}
